import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Badge,
  HStack,
  Button,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Icon,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Skeleton,
} from "@chakra-ui/react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { gql, useQuery } from "@apollo/client";
import {
  ExternalLinkIcon,
  CalendarIcon,
  TimeIcon,
  InfoIcon,
} from "@chakra-ui/icons";
import {
  FaVideo,
  FaMoneyBillWave,
  FaCheckCircle,
  FaExclamationCircle,
} from "react-icons/fa";

import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { useAuth } from "../../contexts/AuthContext";

// GraphQL query to get booking details by ID
const GET_BOOKING_BY_ID = gql`
  query GetBookingById($bookingId: String!) {
    bookingById(bookingId: $bookingId) {
      id
      bookingTime
      startTime
      endTime
      status
      sendReminders
      cancellationReason
      payment {
        status
        method
        amount
        currency
        paidAt
      }
      sessionType {
        id
        name
        description
        format
        duration
        price
        color
        meetingLink
      }
      client {
        id
        fName
        lName
        email
        phoneNumber
      }
      practitionerId
    }
  }
`;

// Add query to get practitioner details
const GET_PRACTITIONER_BY_ID = gql`
  query PractitionerById($id: ID!) {
    practionerById(id: $id) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Helper function to format time
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Helper to get status color
const getStatusColor = (status: string): string => {
  switch (status) {
    case "CONFIRMED":
      return "blue";
    case "COMPLETED":
      return "green";
    case "CANCELLED":
      return "red";
    case "PENDING":
      return "yellow";
    default:
      return "gray";
  }
};

// Helper to get payment status color
const getPaymentStatusColor = (status: string): string => {
  switch (status) {
    case "COMPLETED":
      return "green";
    case "PENDING":
      return "yellow";
    case "PROCESSING":
      return "blue";
    case "FAILED":
      return "red";
    case "REFUNDED":
      return "purple";
    default:
      return "gray";
  }
};

// Component to display countdown
const CountdownTimer = ({ targetDate, onComplete }: { targetDate: Date; onComplete: () => void }) => {
  const calculateTimeLeft = () => {
    const difference = targetDate.getTime() - new Date().getTime();

    if (difference <= 0) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isCompleted: true,
      };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
      isCompleted: false,
    };
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      
      // Call onComplete when timer finishes
      if (newTimeLeft.isCompleted && !timeLeft.isCompleted) {
        onComplete();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate, timeLeft.isCompleted, onComplete]);

  if (timeLeft.isCompleted) {
    return (
      <Box
        p={4}
        bg="green.100"
        borderRadius="md"
        borderLeft="4px solid"
        borderColor="green.500"
      >
        <HStack>
          <Icon as={FaCheckCircle} color="green.500" />
          <Text fontWeight="bold" color="green.700">
            The session is now live!
          </Text>
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      p={4}
      bg="blue.50"
      borderRadius="md"
      borderLeft="4px solid"
      borderColor="blue.500"
    >
      <Text fontWeight="bold" mb={3} color="blue.700">
        Session starts in:
      </Text>
      <SimpleGrid columns={4} spacing={2} textAlign="center">
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color="blue.600">
            {timeLeft.days}
          </Box>
          <Text fontSize="xs" color="blue.600">
            days
          </Text>
        </Box>
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color="blue.600">
            {timeLeft.hours}
          </Box>
          <Text fontSize="xs" color="blue.600">
            hours
          </Text>
        </Box>
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color="blue.600">
            {timeLeft.minutes}
          </Box>
          <Text fontSize="xs" color="blue.600">
            minutes
          </Text>
        </Box>
        <Box>
          <Box fontSize="2xl" fontWeight="bold" color="blue.600">
            {timeLeft.seconds}
          </Box>
          <Text fontSize="xs" color="blue.600">
            seconds
          </Text>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export const BookingDetails = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isLive, setIsLive] = useState(false);

  const { data, loading, error } = useQuery(GET_BOOKING_BY_ID, {
    variables: { bookingId },
    skip: !bookingId,
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("Error fetching booking details:", error);
      toast({
        title: "Error",
        description: "Failed to load booking details. " + error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Fetch practitioner details when practitionerId is available
  const { data: practitionerData, loading: practitionerLoading } = useQuery(GET_PRACTITIONER_BY_ID, {
    variables: { id: data?.bookingById?.practitionerId || "" },
    skip: !data?.bookingById?.practitionerId,
    onError: (error) => {
      console.error("Error fetching practitioner details:", error);
    }
  });

  const booking = data?.bookingById;
  const sessionType = booking?.sessionType;
  const practitioner = practitionerData?.practionerById;
  
  // Update this logic to also consider our local state
  const isSessionLive =
    isLive || (booking?.startTime && new Date(booking.startTime) <= new Date());
    
  // Add check if session is over (1.5 hours after start time)
  const isSessionOver = booking?.startTime && 
    (new Date().getTime() - new Date(booking.startTime).getTime() > 1.5 * 60 * 60 * 1000);

  // Handler for timer completion
  const handleTimerComplete = () => {
    setIsLive(true);
  };

  /* Access restriction removed to allow public viewing of bookings */

  if (loading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.md" py={8}>
          <VStack spacing={6} align="stretch">
            <Skeleton height="40px" width="70%" />
            <Skeleton height="200px" />
            <Skeleton height="120px" />
            <Skeleton height="150px" />
          </VStack>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  if (error || !booking) {
    return (
      <>
        <NavbarWithCallToAction />
        <Container maxW="container.md" py={8}>
          <Box
            p={5}
            bg="red.50"
            borderRadius="md"
            borderLeft="4px solid"
            borderColor="red.500"
          >
            <Heading size="md" mb={3}>
              Error Loading Booking
            </Heading>
            <Text>{error ? error.message : "Booking not found"}</Text>
            <Button
              as={Link}
              to="/sessions"
              mt={4}
              colorScheme="blue"
              size="sm"
            >
              Return to Sessions
            </Button>
          </Box>
        </Container>
        <FooterWithFourColumns />
      </>
    );
  }

  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.lg" py={8}>
        <VStack spacing={6} align="stretch">
          <Box>
            <Heading size="lg" mb={2}>
              Booking Details
            </Heading>
            <Text color="gray.600">Reference: {booking.id}</Text>
          </Box>

          {/* Meeting Participants */}
          <Flex 
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 6 }}
            bg="white"
            p={5}
            borderRadius="lg"
            boxShadow="md"
            border="1px solid"
            borderColor="gray.200"
          >
            {/* Practitioner Details */}
            <Box flex="1" p={4} bg="blue.50" borderRadius="md" boxShadow="sm">
              <Heading size="sm" mb={2} color="blue.700">Practitioner</Heading>
              {practitionerLoading ? (
                <VStack align="start" spacing={2}>
                  <Skeleton height="20px" width="150px" />
                  <Skeleton height="20px" width="180px" />
                  <Skeleton height="20px" width="130px" />
                </VStack>
              ) : practitioner ? (
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">
                    {practitioner.fName} {practitioner.lName}
                  </Text>
                  {practitioner.businessName && (
                    <Text fontSize="sm" color="gray.600">{practitioner.businessName}</Text>
                  )}
                  {practitioner.email && (
                    <Text fontSize="sm">{practitioner.email}</Text>
                  )}
                  {/* {practitioner.phoneNumber && (
                    <Text fontSize="sm">{practitioner.phoneNumber}</Text>
                  )} */}
                </VStack>
              ) : (
                <Text color="gray.500">Practitioner information not available</Text>
              )}
            </Box>

            {/* Client Details Box */}
            <Box flex="1" p={4} bg="green.50" borderRadius="md" boxShadow="sm">
              <Heading size="sm" mb={2} color="green.700">Client</Heading>
              {booking.client ? (
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">
                    {booking.client.fName} {booking.client.lName}
                  </Text>
                  {booking.client.email && (
                    <Text fontSize="sm">{booking.client.email}</Text>
                  )}
                  {/* {booking.client.phoneNumber && (
                    <Text fontSize="sm">{booking.client.phoneNumber}</Text>
                  )} */}
                </VStack>
              ) : (
                <Text color="gray.500">Client information not available</Text>
              )}
            </Box>
          </Flex>

          {/* Countdown Timer */}
          {booking.status === "CONFIRMED" &&
            booking.startTime &&
            new Date(booking.startTime) > new Date() && (
              <CountdownTimer 
                targetDate={new Date(booking.startTime)} 
                onComplete={handleTimerComplete}
              />
            )}

          {/* Meeting Link Button (shows only when session is live and confirmed) */}
          {booking.status === "CONFIRMED" &&
            isSessionLive &&
            !isSessionOver &&
            sessionType?.meetingLink && (
              <Box p={4} bg="green.100" borderRadius="md" textAlign="center">
                <Heading size="md" mb={3}>
                  Your Session is Live!
                </Heading>
                <Button
                  as="a"
                  href={sessionType.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  colorScheme="green"
                  size="lg"
                  leftIcon={<FaVideo />}
                  rightIcon={<ExternalLinkIcon />}
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md" }}
                  transition="all 0.2s"
                >
                  Join Meeting Now
                </Button>
              </Box>
            )}
            
          {/* Show Meeting Over message when session is over */}
          {booking.status === "CONFIRMED" &&
            isSessionOver && (
              <Box p={4} bg="gray.100" borderRadius="md" textAlign="center">
                <Heading size="md" mb={3} color="gray.600">
                  Meeting is Over
                </Heading>
                <Text>
                  This session has ended. Thank you for attending.
                </Text>
              </Box>
            )}

          {/* Booking Status */}
          <Card variant="outline" bg="white" borderRadius="lg" boxShadow="sm">
            <CardHeader bg={`${getStatusColor(booking.status)}.50`} py={3}>
              <HStack>
                <Icon
                  as={
                    booking.status === "CONFIRMED"
                      ? FaCheckCircle
                      : booking.status === "CANCELLED"
                        ? FaExclamationCircle
                        : InfoIcon
                  }
                  color={`${getStatusColor(booking.status)}.500`}
                />
                <Heading size="md">
                  Status:{" "}
                  <Badge
                    colorScheme={getStatusColor(booking.status)}
                    fontSize="md"
                    py={1}
                    px={2}
                  >
                    {booking.status}
                  </Badge>
                </Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Stat>
                    <StatLabel>Session Date</StatLabel>
                    <StatNumber fontSize="lg">
                      {booking.startTime
                        ? formatDate(booking.startTime)
                        : "N/A"}
                    </StatNumber>
                    <StatHelpText>
                      <HStack>
                        <CalendarIcon />
                        <Text>Booked on {formatDate(booking.bookingTime)}</Text>
                      </HStack>
                    </StatHelpText>
                  </Stat>
                </Box>

                <Box>
                  <Stat>
                    <StatLabel>Session Time</StatLabel>
                    <StatNumber fontSize="lg">
                      {booking.startTime
                        ? formatTime(booking.startTime)
                        : "N/A"}{" "}
                      - {booking.endTime ? formatTime(booking.endTime) : "N/A"}
                    </StatNumber>
                    <StatHelpText>
                      <HStack>
                        <TimeIcon />
                        <Text>{sessionType?.duration} minutes</Text>
                      </HStack>
                    </StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>

              {booking.cancellationReason && (
                <Box mt={4} p={3} bg="red.50" borderRadius="md">
                  <Text fontWeight="bold">Cancellation Reason:</Text>
                  <Text>{booking.cancellationReason}</Text>
                </Box>
              )}
            </CardBody>
          </Card>

          {/* Session Details */}
          <Card variant="outline" bg="white" borderRadius="lg" boxShadow="sm">
            <CardHeader bg="purple.50" py={3}>
              <Heading size="md">Session Details</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <Flex justify="space-between" flexWrap="wrap">
                  <Box flex="1" minW="200px">
                    <Heading size="sm" mb={1}>
                      Session Type
                    </Heading>
                    <Text fontSize="xl" fontWeight="bold">
                      {sessionType?.name || "N/A"}
                    </Text>
                  </Box>

                  <Flex gap={2} flexWrap="wrap">
                    <Badge colorScheme="purple" fontSize="md" px={2} py={1}>
                      {sessionType?.format || "N/A"}
                    </Badge>
                    <Badge colorScheme="blue" fontSize="md" px={2} py={1}>
                      {sessionType?.duration || "N/A"} min
                    </Badge>
                    <Badge colorScheme="green" fontSize="md" px={2} py={1}>
                      ${sessionType?.price || "N/A"}
                    </Badge>
                  </Flex>
                </Flex>

                {sessionType?.description && (
                  <Box>
                    <Heading size="sm" mb={1}>
                      Description
                    </Heading>
                    <Text>{sessionType.description}</Text>
                  </Box>
                )}

                {/* Display meeting link information if available but not yet live */}
                {sessionType?.meetingLink &&
                  !isSessionLive &&
                  booking.status === "CONFIRMED" && (
                    <Box p={3} bg="blue.50" borderRadius="md">
                      <Heading size="sm" mb={1}>
                        Online Meeting
                      </Heading>
                      <Text mb={2}>
                        This session will take place online. The meeting link
                        will be available when the session starts.
                      </Text>
                    </Box>
                  )}
              </VStack>
            </CardBody>
          </Card>

          {/* Payment Details */}
          <Card variant="outline" bg="white" borderRadius="lg" boxShadow="sm">
            <CardHeader bg="green.50" py={3}>
              <HStack>
                <Icon as={FaMoneyBillWave} color="green.500" />
                <Heading size="md">Payment Details</Heading>
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box>
                  <Heading size="sm" mb={1}>
                    Payment Status
                  </Heading>
                  <Badge
                    colorScheme={getPaymentStatusColor(booking.payment?.status)}
                    fontSize="md"
                    px={2}
                    py={1}
                  >
                    {booking.payment?.status || "N/A"}
                  </Badge>
                </Box>

                <Box>
                  <Heading size="sm" mb={1}>
                    Amount
                  </Heading>
                  <Text fontSize="xl" fontWeight="bold">
                    ${booking.payment?.amount || "N/A"}{" "}
                    {booking.payment?.currency || "USD"}
                  </Text>
                </Box>

                <Box>
                  <Heading size="sm" mb={1}>
                    Payment Method
                  </Heading>
                  <Text>{booking.payment?.method || "N/A"}</Text>
                </Box>

                {booking.payment?.paidAt && (
                  <Box>
                    <Heading size="sm" mb={1}>
                      Payment Date
                    </Heading>
                    <Text>
                      {formatDate(booking.payment.paidAt)} at{" "}
                      {formatTime(booking.payment.paidAt)}
                    </Text>
                  </Box>
                )}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Actions */}
          <HStack spacing={4} justify="center" mt={4}>
            <Button as={Link} to="/sessions" colorScheme="gray">
              Back to Sessions
            </Button>

            {booking.status === "CONFIRMED" && (
              <Button
                as={Link}
                to={`/sessions/bookings/${booking.id}/cancel`}
                colorScheme="red"
                variant="outline"
              >
                Cancel Booking
              </Button>
            )}
          </HStack>
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};
